<template>
  <div class="mt-4 flex flex-col">
    <span class="secondary_color text-[13px]">Task Assignee</span>
    <div class="mt-2 flex items-center gap-2">
      <a-checkbox v-model:checked="localAssignToOwner">
        <span class="text-[14px] text-sm text_color">Assign to Owner</span>
      </a-checkbox>
    </div>

    <span class="secondary_color text-[13px] mt-5">Task Priority</span>
    <a-select
      class="mt-2"
      v-model:value="modelValue.task_priority"
      placeholder="Select Priority"
    >
      <template #suffixIcon>
        <ThemeImage
          :icon="imageNames.BOTTOM_ARROW"
          alt="bottom_white_arrow"
          class="size-4"
        />
      </template>
      <a-select-option value="LOW">Low</a-select-option>
      <a-select-option value="MEDIUM">Medium</a-select-option>
      <a-select-option value="HIGH">High</a-select-option>
    </a-select>

    <a-checkbox class="mt-4" v-model:checked="modelValue.pause_until_complete">
      Pause {{ featureNames.SEQUENCES }} until task is completed
    </a-checkbox>

    <span class="secondary_color text-[13px] mt-5"
      >Task Description (Optional)</span
    >
    <div class="relative mt-2">
      <a-textarea
        ref="descriptionRef"
        v-model:value="modelValue.task_description"
        class="textarea_large !pr-10"
        :rows="4"
        placeholder="Enter task description"
        @click="updateDescriptionCursorPosition"
        @keyup="updateDescriptionCursorPosition"
        @input="updateDescriptionCursorPosition"
      />
      <div class="absolute bottom-2 right-2 flex items-center">
        <PersonalizationDropdown
          @apply-personalization="applyPersonalizationToDescription"
          position="topend"
          :iconOnly="true"
          :onHover="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref } from "vue";
import { featureNames } from "@/src/constants/globalConstants";
import { imageNames } from "@/src/constants/imageConstants.js";
import PersonalizationDropdown from "@/src/components/PersonalizationDropdown.vue";

const props = defineProps({
  modelValue: { type: Object, required: true },
});

const emit = defineEmits(["update:modelValue"]);

const localAssignToOwner = computed({
  get: () => {
    return props.modelValue.assign_to_owner !== undefined
      ? props.modelValue.assign_to_owner
      : true;
  },
  set: (value) => {
    const updatedValue = { ...props.modelValue, assign_to_owner: value };
    if (value) {
      updatedValue.assigned_to_id = null;
    }

    emit("update:modelValue", updatedValue);
  },
});

const descriptionRef = ref(null);
const descriptionCursorPosition = ref(0);

const getDescriptionTextarea = (shouldFocus = false) => {
  if (!descriptionRef.value) return null;
  try {
    if (shouldFocus) {
      descriptionRef.value.focus?.();
    }
    const root = descriptionRef.value.$el || descriptionRef.value;
    if (root && root.tagName === "TEXTAREA") return root;
    return root?.querySelector?.("textarea") || null;
  } catch (e) {
    return null;
  }
};

const updateDescriptionCursorPosition = () => {
  const el = getDescriptionTextarea();
  if (el && typeof el.selectionStart === "number") {
    descriptionCursorPosition.value = el.selectionStart;
  }
};

const applyPersonalizationToDescription = (tag) => {
  updateDescriptionCursorPosition();
  const currentValue = props.modelValue.task_description || "";
  const cursorPos = descriptionCursorPosition.value || 0;
  const safeCursorPos = Math.max(0, Math.min(cursorPos, currentValue.length));

  const updated =
    currentValue.slice(0, safeCursorPos) + tag + currentValue.slice(safeCursorPos);
  emit("update:modelValue", { ...props.modelValue, task_description: updated });

  const newCursorPos = safeCursorPos + tag.length;
  descriptionCursorPosition.value = newCursorPos;

  nextTick(() => {
    const el = getDescriptionTextarea(true);
    if (el) {
      el.setSelectionRange(newCursorPos, newCursorPos);
    }
  });
};
</script>
